import { USER_PERMISSIONS } from "@hyro/dashboard-permissions";
import {
  BuildOutlined as BuildOutlinedIcon,
  ExploreOutlined as ExploreOutlinedIcon,
  HomeOutlined as HomeOutlinedIcon,
  OfflineBoltOutlined as OfflineBoltOutlinedIcon
} from "@mui/icons-material";

import AssistantIcon from "../../icons/AssistantIcon";
import BranchDeployerIcon from "../../icons/BranchDeployerIcon";
import ClockIconMenu from "../../icons/ClockIconMenu";
import CloudCacheIcon from "../../icons/CloudCache";
import ConversationsIcon from "../../icons/ConversationsIcon";
import DashboardSettingsIcon from "../../icons/DashboardSettingsIcon";
import EditorIcon from "../../icons/EditorIcon";
import FolderIcon from "../../icons/FolderIcon";
import GridIcon from "../../icons/GridIcon";
import InsightsIcon from "../../icons/InsightsIcon";
import IntegrationIcon from "../../icons/IntegrationIcon";
import OutboundIcon from "../../icons/OutboundIcon";
import {
  ACCOUNT_MANAGEMENT,
  ALL_ASSISTANTS,
  AUDIT_LOG, BACK_OFFICE,
  BRANCH_DEPLOYER,
  BUILDING_BLOCKS,
  CHAT_INSIGHTS,
  CONVERSATIONS,
  CORE_HYRO_KPIS,
  ENVIRONMENTS,
  HOME,
  INSIGHTS,
  INTEGRATIONS,
  KNOWLEDGE_DEBUGGER,
  KNOWLEDGE_EXPLORER,
  PIPELINE_PERFORMANCE,
  AGENT_PERFORMANCE,
  SETTINGS,
  SKILLS,
  VOICE_INSIGHTS,
  CAMPAIGN_MANAGER,
  DRAFTS,
  FAILURES,
  UTTERANCE_ORDER,
  CLOUD_CACHE_UPDATE,
  DATA_MAPPING,
  CONFIGURATION_EDITOR
} from "@consts";

const CONVERSATIONS_MIN = import.meta.env.VITE_ENV === ENVIRONMENTS.PRODUCTION ? "minute" : "minute";

const filterNavItemByPermission = hasPermission => (navItem) => {
  if (navItem.children) {
    navItem.children = navItem.children.filter(filterNavItemByPermission(hasPermission));
  }
  return navItem.requiredPermission
    ? hasPermission(navItem.requiredPermission)
    : true;
};

const getGeneralNavItems = (
  accountId,
  selectedAssistantId,
  selectedConversationId = null
) => [
  {
    key: HOME,
    text: "Overview",
    icon: HomeOutlinedIcon,
    url: `/${accountId}/${selectedAssistantId}/${HOME}`,
    requiredPermission: USER_PERMISSIONS.HOME_PAGE.READ
  },
  {
    key: INSIGHTS,
    text: "Insights",
    description: `Loading data may take time, it refreshes every ${CONVERSATIONS_MIN}.`,
    url: `/${accountId}/${selectedAssistantId}/${INSIGHTS}`,
    icon: InsightsIcon,
    requiredPermission: USER_PERMISSIONS.INSIGHTS_PAGE.READ
  },
  {
    key: CONVERSATIONS,
    text: `Conversation: ${selectedConversationId}`,
    icon: ConversationsIcon,
    url: `/${accountId}/${selectedAssistantId}/${CONVERSATIONS}/${selectedConversationId}`,
    requiredPermission: "HACK_TO_NOT_SHOW_IN_NAV_DONT_REMOVE_ME",
    showTimezoneDisplay: true
  },
  {
    key: CONVERSATIONS,
    text: "Conversations",
    description: `The data is refreshed every ${CONVERSATIONS_MIN}.`,
    icon: ConversationsIcon,
    url: `/${accountId}/${selectedAssistantId}/${CONVERSATIONS}`,
    requiredPermission: USER_PERMISSIONS.CONVERSATIONS_PAGE.READ,
    showTimezoneDisplay: true
  },
  {
    key: SKILLS,
    text: "Skills",
    icon: OfflineBoltOutlinedIcon,
    url: `/${accountId}/${selectedAssistantId}/${SKILLS}`,
    requiredPermission: USER_PERMISSIONS.SKILLS_PAGE.READ,
    isCodelessRoute: true
  },
  {
    key: BUILDING_BLOCKS,
    text: "Building Blocks",
    icon: GridIcon,
    url: `/${accountId}/${selectedAssistantId}/${BUILDING_BLOCKS}`,
    requiredPermission: USER_PERMISSIONS.BUILDING_BLOCKS_PAGE.READ,
    isCodelessRoute: true
  },
  {
    key: INTEGRATIONS,
    text: "Integrations",
    icon: IntegrationIcon,
    url: `/${accountId}/${selectedAssistantId}/${INTEGRATIONS}`,
    requiredPermission: USER_PERMISSIONS.INTEGRATIONS_PAGE.READ,
    isCodelessRoute: true
  },
  {
    key: CAMPAIGN_MANAGER,
    text: "Campaign Manager",
    icon: OutboundIcon,
    url: `/${accountId}/${selectedAssistantId}/${CAMPAIGN_MANAGER}`
  }
];

const getAdminNavItems = () => [
  {
    key: ALL_ASSISTANTS,
    text: "All Agents",
    icon: AssistantIcon,
    url: `/admin/${ALL_ASSISTANTS}`
  },
  {
    key: ACCOUNT_MANAGEMENT,
    text: "Account Management",
    icon: FolderIcon,
    url: `/admin/${ACCOUNT_MANAGEMENT}`
  },
  {
    key: BRANCH_DEPLOYER,
    text: "Branch Deployer",
    icon: BranchDeployerIcon,
    url: `/admin/${BRANCH_DEPLOYER}`
  },
  {
    key: INSIGHTS,
    text: "Insights",
    icon: InsightsIcon,
    children: [
      {
        key: `${INSIGHTS}/${VOICE_INSIGHTS}`,
        text: "Voice",
        url: `/admin/insights/${VOICE_INSIGHTS}`
      },
      {
        key: `${INSIGHTS}/${CHAT_INSIGHTS}`,
        text: "Chat",
        url: `/admin/insights/${CHAT_INSIGHTS}`
      },
      {
        key: `${INSIGHTS}/${PIPELINE_PERFORMANCE}`,
        text: "Pipeline Performance",
        url: `/admin/insights/${PIPELINE_PERFORMANCE}`
      },
      {
        key: `${INSIGHTS}/${CORE_HYRO_KPIS}`,
        text: "Core Hyro KPIs",
        url: `/admin/insights/${CORE_HYRO_KPIS}`
      },
      {
        key: `${INSIGHTS}/${AGENT_PERFORMANCE}`,
        text: "Agent Performance",
        url: `/admin/insights/${AGENT_PERFORMANCE}`
      }
    ]
  },
  {
    key: BACK_OFFICE,
    text: "Back Office",
    icon: BuildOutlinedIcon,
    children: [
      {
        key: `${BACK_OFFICE}/${DRAFTS}`,
        text: "Drafts",
        url: `/admin/back-office/${DRAFTS}`
      },
      {
        key: `${BACK_OFFICE}/${FAILURES}`,
        text: "Failures",
        url: `/admin/back-office/${FAILURES}`
      },
      {
        key: `${BACK_OFFICE}/${UTTERANCE_ORDER}`,
        text: "Utterance Order",
        url: `/admin/back-office/${UTTERANCE_ORDER}`,
        requiredPermission: USER_PERMISSIONS.ADMIN_PANEL.ORDER_COLLECTIONS.READ
      },
      {
        key: `${BACK_OFFICE}/${KNOWLEDGE_DEBUGGER}`,
        text: "Debugger",
        url: `/admin/back-office/${KNOWLEDGE_DEBUGGER}`
      },
      {
        key: `${BACK_OFFICE}/${KNOWLEDGE_EXPLORER}`,
        text: "Explorer",
        url: `/admin/back-office/${KNOWLEDGE_EXPLORER}`
      },
      {
        key: `${BACK_OFFICE}/${DATA_MAPPING}`,
        text: "Data Mapping",
        url: `/admin/back-office/${DATA_MAPPING}`
      }
    ]
  },
  {
    key: AUDIT_LOG,
    text: "Audit Log",
    icon: ClockIconMenu,
    url: `/admin/${AUDIT_LOG}`,
    requiredPermission: USER_PERMISSIONS.AUDIT_LOG.READ
  },
  {
    key: SETTINGS,
    text: "Settings",
    icon: DashboardSettingsIcon,
    url: `/admin/${SETTINGS}`
  }
];

const getAccountNavItems = (accountId) => [
  {
    key: ALL_ASSISTANTS,
    text: "All Agents",
    icon: AssistantIcon,
    url: `/${accountId}`,
    requiredPermission: USER_PERMISSIONS.ACCOUNT_ASSISTANTS_PAGE.READ
  },
  {
    key: CAMPAIGN_MANAGER,
    text: "Campaign Manager",
    icon: OutboundIcon,
    url: `/${accountId}/${CAMPAIGN_MANAGER}`,
    requiredPermission: USER_PERMISSIONS.CAMPAIGN_MANAGER.READ
  }
];

const getHyroNavItems = (accountId, selectedAssistantId) => [
  {
    key: KNOWLEDGE_EXPLORER,
    text: "Knowledge Explorer",
    icon: ExploreOutlinedIcon,
    url: `/${accountId}/${selectedAssistantId}/${KNOWLEDGE_EXPLORER}`,
    requiredPermission: USER_PERMISSIONS.KNOWLEDGE_EXPLORER_PAGE.READ
  },
  {
    key: CLOUD_CACHE_UPDATE,
    text: "Cloud Cache Editor",
    icon: CloudCacheIcon,
    url: `/${accountId}/${selectedAssistantId}/${CLOUD_CACHE_UPDATE}`,
    requiredPermission: USER_PERMISSIONS.CLOUD_CACHE_EDITOR_PAGE.READ
  },
  {
    key: CONFIGURATION_EDITOR,
    text: "Configuration Editor",
    icon: EditorIcon,
    url: `/${accountId}/${selectedAssistantId}/${CONFIGURATION_EDITOR}`,
    requiredPermission: USER_PERMISSIONS.CLOUD_CACHE_EDITOR_PAGE.READ
  },
  {
    key: AUDIT_LOG,
    text: "Audit Log",
    icon: ClockIconMenu,
    url: `/${accountId}/${selectedAssistantId}/${AUDIT_LOG}`,
    requiredPermission: USER_PERMISSIONS.AUDIT_LOG.READ
  }
];
export {
  filterNavItemByPermission,
  getGeneralNavItems,
  getHyroNavItems,
  getAdminNavItems,
  getAccountNavItems
};
